import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';
import {
  tokenService,
  SESSION_END_EVENT,
  type SessionEndReason,
} from '@features/authentication/services/tokenService';
import { withRefreshLock } from '@features/authentication/services/sessionLock';
import type { ApiResponse } from '../types';
import type { AuthenticationDataDto } from '@features/authentication/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// 86d3va61r — THE governing rule for this interceptor: a request the user did not initiate must never
// produce a toast. The original defect was the app shell fetching an admin-only endpoint on load, so
// every non-admin got an unsolicited "Access Denied" popup for something they never clicked. Removing
// that one call is not enough — any background fetch can hit a 403/503 and pop the same way.
//
// Mark such calls `{ background: true }` and they fail silently: the caller's own store already holds
// an error field for whatever UI wants to show it inline. Anything the user actually triggered (a click,
// a navigation, a submit) is left interactive and still toasts.
declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * True for fetches the user did not initiate (app-shell loads, polls, prefetches).
     * Suppresses global error toasts only — status handling, retries and rejection are unchanged.
     */
    background?: boolean;
    /**
     * 86d3zp17w — never attempt a token refresh for this request.
     *
     * Required by logout: its body carries a refresh token read before dispatch, so if the
     * interceptor refreshed and rotated mid-flight, the retried logout would revoke the PARENT and
     * leave the live successor valid for its full lifetime. Also the right flag for any request that
     * must remain readable while the refresh path itself is misbehaving.
     */
    skipAuthRefresh?: boolean;
  }
}

export interface RefreshResult {
  accessToken: string;
  /** True when a sibling tab did the work and this tab simply picked up the result. */
  adopted: boolean;
}

// 86d3zp17w — refresh is single-flight per tab AND across tabs.
//
// `refreshPromise` coalesces callers inside THIS tab. It is a module-scoped variable, so it is
// per-JS-context and does nothing across tabs — the comment that used to claim otherwise is why this
// race went unnoticed. `withRefreshLock` supplies the cross-tab half. Both are kept: the in-tab
// promise means the common case never touches the lock at all.
let refreshPromise: Promise<RefreshResult | null> | null = null;

function refreshAccessToken(): Promise<RefreshResult | null> {
  if (refreshPromise) return refreshPromise;

  // Snapshot BEFORE queueing for the lock, so that after acquiring it we can tell whether a sibling
  // tab rotated the token while we were waiting.
  const presentedAtEntry = tokenService.getRefreshToken();

  refreshPromise = withRefreshLock(async () => {
    const current = tokenService.getRefreshToken();
    if (!current) return null;

    // THE DOUBLE-CHECK — load-bearing. Without it the lock merely SERIALISES N refreshes instead of
    // preventing N−1 of them: every queued tab would still POST, and every POST after the first
    // presents a refresh token the server has already consumed. Mirrors the mobile client's
    // AuthenticatedHttpClientHandler.cs:111, which is why mobile never had this bug.
    if (presentedAtEntry !== null && current !== presentedAtEntry) {
      const adopted = tokenService.getAccessToken();
      if (adopted) return { accessToken: adopted, adopted: true };
    }

    try {
      const { data } = await axios.post<ApiResponse<AuthenticationDataDto>>(
        `${API_BASE_URL}/authentication/refresh`,
        { refreshToken: current },
        // Bare axios.post inherits no instance config, and the axios default is 0 = wait forever.
        { timeout: 6000 }
      );

      if (data.success && data.data) {
        tokenService.setTokens(data.data);
        return { accessToken: data.data.accessToken, adopted: false };
      }
      return null;
    } catch (error) {
      // Before this counts as a failure: did a sibling rotate the token while our POST was in flight?
      // If storage no longer holds what we sent, the session is ALIVE and we are just holding a copy
      // that was replaced milliseconds ago. Re-reading here is what turns a lost race from a mass
      // logout into a no-op.
      const after = tokenService.getRefreshToken();
      if (after && after !== current) {
        const adopted = tokenService.getAccessToken();
        if (adopted) return { accessToken: adopted, adopted: true };
      }
      throw error;
    }
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * End the session because it is genuinely over — never because one request failed recoverably.
 *
 * Writes the explicit marker (which siblings observe via the `storage` event) and raises the in-tab
 * event (which the `storage` event does NOT deliver to the originating tab). Deliberately does not
 * navigate: `window.location.href` was a full document reload that destroyed unsaved work and reset
 * the route. useLogoutSync owns the navigation, through React Router.
 */
function endSessionLocally(reason: SessionEndReason): void {
  tokenService.endSession(reason);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_END_EVENT, { detail: { reason } }));
  }
}

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handles token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      /**
       * Replaces the old boolean `_retry`. Two DIFFERENT recoveries can each legitimately replay a
       * request — a stale-token replay (costs no refresh) and a post-refresh replay — and a single
       * boolean made the first consume the budget of the second.
       */
      _authRetries?: number;
    };

    // See the `background` declaration above — suppresses toasts, nothing else.
    const isBackground = originalRequest?.background === true;

    // Handle 403 - access denied
    if (error.response?.status === 403) {
      if (!isBackground) {
        const apiMessage = error.response?.data?.message;
        toast.error('Access Denied', {
          description: apiMessage || 'You do not have permission to perform this action.',
        });
      }
      return Promise.reject(error);
    }

    // Handle 503 - NetSuite concurrency gate busy (86d3n2x8u). The server returns 503 + a positive-integer
    // Retry-After ONLY for a gate-busy reject (the NetSuite call never executed → safe to retry). We treat
    // that positive Retry-After as the busy signal (review#5): a proxy/gateway 503 during a deploy has no
    // valid Retry-After, so it falls through to normal error handling instead of being retried and
    // mislabeled "NetSuite busy". And we auto-retry ONLY idempotent methods — a bare 503 on a write must
    // never be re-sent (it could have committed; the transport layer already refuses to retry writes).
    if (error.response?.status === 503) {
      const retryAfterSec = Number(error.response.headers?.['retry-after']);
      const isBusySignal = Number.isFinite(retryAfterSec) && retryAfterSec > 0;
      if (isBusySignal) {
        const method = (originalRequest.method ?? 'get').toLowerCase();
        const isIdempotent =
          method === 'get' || method === 'head' || method === 'options';
        if (isIdempotent) {
          const busyRequest = originalRequest as InternalAxiosRequestConfig & {
            _busyRetries?: number;
          };
          const MAX_BUSY_RETRIES = 3;
          busyRequest._busyRetries = (busyRequest._busyRetries ?? 0) + 1;
          if (busyRequest._busyRetries <= MAX_BUSY_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, retryAfterSec * 1000));
            return apiClient(busyRequest);
          }
        }
        // Non-idempotent (never auto-retried), or idempotent retries exhausted: surface a "busy" message.
        if (!isBackground) {
          toast.error('System busy', {
            description: 'NetSuite is busy right now. Please try again in a moment.',
          });
        }
        return Promise.reject(error);
      }

      // 86d3va61r — a 503 WITHOUT a positive Retry-After is not the NetSuite gate. Two cases remain, and
      // they must be told apart, because guessing wrong is visible to every user:
      //
      //   (a) ModuleEnforcementMiddleware blocked the request — a disabled / under-maintenance module. It
      //       writes a JSON ApiResponse carrying the operator's maintenance message. Before this, that
      //       message was dropped on the floor and the screen just died with no explanation.
      //   (b) A proxy/gateway 503 during a deploy or restart. No ApiResponse body at all. MUST stay
      //       silent — toasting here would fire an error at everyone on every deployment.
      //
      // The presence of our own `message` in the body is the discriminator. And even for (a) the toast is
      // interactive-only: /announcements/active is fetched on every app load, so a disabled ANNT module
      // would otherwise toast every user on every page load — the exact defect this ticket removes.
      const moduleMessage = error.response.data?.message;
      if (moduleMessage && !isBackground) {
        toast.error('Unavailable', { description: moduleMessage });
      }
      return Promise.reject(error);
    }

    // Handle 401. NOTE: refresh is single-flight across tabs via withRefreshLock — the module-scoped
    // promise alone never was, despite the comment that used to sit here.
    if (error.response?.status === 401) {
      // 86d3zp17w — NOT every 401 is an expired access token. A JwtBearer challenge has an EMPTY body
      // (Program.cs registers only OnMessageReceived — no OnChallenge). An APPLICATION 401 carries a
      // JSON ApiResponse and means real code ran and rejected the request: a wrong current password
      // (AuthenticationService.cs), or "User identity not found" from a controller. Refreshing and
      // replaying those would re-submit the same wrong password up to three times.
      if (error.response.data) return Promise.reject(error);

      if (originalRequest?.skipAuthRefresh) return Promise.reject(error);

      const attempts = originalRequest._authRetries ?? 0;
      if (attempts >= 2) return Promise.reject(error);
      originalRequest._authRetries = attempts + 1;

      // Recovery 1 — the cheap one. If the token we SENT is no longer the token in storage, a sibling
      // tab refreshed while this request was in flight. The session is fine and this request just
      // carried a stale header, so replay it. Costs no refresh and consumes no rotation.
      const sent = String(originalRequest.headers?.Authorization ?? '');
      const currentAccess = tokenService.getAccessToken();
      if (currentAccess && sent && sent !== `Bearer ${currentAccess}`) {
        originalRequest.headers.Authorization = `Bearer ${currentAccess}`;
        return apiClient(originalRequest);
      }

      // Recovery 2 — actually refresh. This coalesces across tabs and adopts a sibling's result
      // rather than presenting a spent token (see refreshAccessToken).
      try {
        const result = await refreshAccessToken();
        if (result) {
          originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
          return apiClient(originalRequest);
        }
        // No refresh token at all — there is no session to save.
        endSessionLocally('expired');
        return Promise.reject(error);
      } catch (refreshError) {
        // Only a definitive auth failure ends the session. Transient network errors, timeouts and 5xx
        // must not: the token is probably still valid and the user is probably still working.
        // (refreshAccessToken has already re-read storage and adopted a sibling's token if one exists,
        // so reaching here means the session really is gone.)
        const isAuthError =
          refreshError instanceof AxiosError &&
          (refreshError.response?.status === 401 ||
            refreshError.response?.status === 400);
        if (isAuthError) {
          endSessionLocally('expired');
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient, refreshAccessToken };
export type { ApiResponse };
